import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Bot, FileText, Loader2, Plus, RefreshCw, Save, Search } from 'lucide-react';

import { Button } from '../components_dashboard/ui/button';
import { Input } from '../components_dashboard/ui/input';
import { Label } from '../components_dashboard/ui/label';
import { Switch } from '../components_dashboard/ui/switch';
import { Textarea } from '../components_dashboard/ui/textarea';
import { toast } from '../components/CustomToast';
import { useAuth } from '../context/AuthContext';

const apiUrl = process.env.REACT_APP_BACKEND_URL;
const NEW_PROMPT_ID = '__new__';

const createDraft = (prompt) => ({
  id: prompt?.id || '',
  agent_id: prompt?.agent_id || '',
  agent_name: prompt?.agent_name || '',
  status: prompt?.status || '',
  industry_id: prompt?.industry_id || '',
  version_id: prompt?.version_id || '',
  version_name: prompt?.version_name || '',
  system_prompt: prompt?.system_prompt || '',
  first_message: prompt?.first_message || '',
  calendar: !!prompt?.calendar,
  domain: prompt?.domain || 'demo_agent',
  __isNew: false,
});

const createEmptyDraft = () => ({
  id: '',
  agent_id: '',
  agent_name: '',
  status: 'Active',
  industry_id: '',
  version_id: '',
  version_name: 'Version 1',
  system_prompt: '',
  first_message: '',
  calendar: true,
  domain: 'demo_agent',
  __isNew: true,
});

export default function DemoAgentPromptsDashboard() {
  const { token } = useAuth();
  const [prompts, setPrompts] = useState([]);
  const [selectedPromptId, setSelectedPromptId] = useState('');
  const [draft, setDraft] = useState(null);
  const [loadingPrompts, setLoadingPrompts] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const selectedPrompt = useMemo(
    () => prompts.find((entry) => entry.id === selectedPromptId) || null,
    [prompts, selectedPromptId]
  );
  const isCreatingPrompt = Boolean(draft?.__isNew);

  const filteredPrompts = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return prompts;
    return prompts.filter((entry) => (
      String(entry.agent_name || '').toLowerCase().includes(query)
      || String(entry.industry_id || '').toLowerCase().includes(query)
      || String(entry.domain || '').toLowerCase().includes(query)
      || String(entry.system_prompt || '').toLowerCase().includes(query)
      || String(entry.first_message || '').toLowerCase().includes(query)
    ));
  }, [prompts, searchQuery]);

  const fetchPrompts = useCallback(async () => {
    if (!token) return;
    try {
      setLoadingPrompts(true);
      const response = await axios.get(`${apiUrl}/api/v1/admin/demo-agent-prompts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const rows = Array.isArray(response.data?.demo_agents) ? response.data.demo_agents : [];
      setPrompts(rows);
      setSelectedPromptId((current) => {
        if (current === NEW_PROMPT_ID) return current;
        if (current && rows.some((entry) => entry.id === current)) return current;
        return rows[0]?.id || '';
      });
    } catch (error) {
      console.error('Failed to fetch demo agent prompts', error);
      toast.error(error.response?.data?.detail || 'Failed to load demo agent prompts.');
      setPrompts([]);
      setSelectedPromptId('');
    } finally {
      setLoadingPrompts(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPrompts();
  }, [fetchPrompts]);

  useEffect(() => {
    if (selectedPromptId === NEW_PROMPT_ID) {
      setDraft(createEmptyDraft());
    } else {
      setDraft(selectedPrompt ? createDraft(selectedPrompt) : null);
    }
  }, [selectedPrompt, selectedPromptId]);

  const handleCreateDraft = () => {
    setSelectedPromptId(NEW_PROMPT_ID);
  };

  const handleFieldChange = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
  };

  const handleSave = async () => {
    if (!draft || !selectedPromptId) return;

    if (!String(draft.agent_name || '').trim()) {
      toast.error('Agent name is required.');
      return;
    }
    if (!String(draft.industry_id || '').trim()) {
      toast.error('Industry ID is required.');
      return;
    }
    if (!isCreatingPrompt && !String(draft.domain || '').trim()) {
      toast.error('Domain is required.');
      return;
    }

    try {
      setSaving(true);
      const payload = isCreatingPrompt
        ? {
          agent_name: draft.agent_name,
          industry_id: draft.industry_id,
          system_prompt: draft.system_prompt || '',
          first_message: draft.first_message || '',
        }
        : {
          agent_id: draft.agent_id || null,
          agent_name: draft.agent_name,
          status: draft.status || null,
          industry_id: draft.industry_id,
          version_id: draft.version_id || null,
          version_name: draft.version_name || null,
          system_prompt: draft.system_prompt || '',
          first_message: draft.first_message || '',
          calendar: !!draft.calendar,
          domain: draft.domain,
        };
      const response = isCreatingPrompt
        ? await axios.post(`${apiUrl}/api/v1/admin/demo-agent-prompts`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        })
        : await axios.patch(
          `${apiUrl}/api/v1/admin/demo-agent-prompts/${selectedPromptId}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      const updatedPrompt = response.data;
      setDraft(createDraft(updatedPrompt));
      setSelectedPromptId(updatedPrompt.id);
      setPrompts((current) => (
        isCreatingPrompt
          ? [updatedPrompt, ...current]
          : current.map((entry) => (entry.id === updatedPrompt.id ? updatedPrompt : entry))
      ));
      toast.success(isCreatingPrompt ? 'Demo agent prompt created.' : 'Demo agent prompt updated.');
    } catch (error) {
      console.error('Failed to save demo agent prompt', error);
      toast.error(error.response?.data?.detail || 'Failed to save demo agent prompt.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col p-6">
      

      <main className="flex-1 min-h-0 p-6 overflow-y-auto  lg:p-8">
        <div className="mx-auto max-w-[1600px] space-y-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-violet-100 bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700">
                <Bot className="w-3.5 h-3.5" />
                Demo Agent Prompts
              </div>
              <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-900">Demo Agent Prompt Manager</h1>
              <p className="mt-1 text-slate-500">Edit prompts from the demo_agent_prompt collection.</p>
            </div>

            <Button
              variant="outline"
              onClick={fetchPrompts}
              disabled={loadingPrompts}
              className="border-slate-200 text-slate-700 hover:bg-slate-100"
            >
              {loadingPrompts ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Refresh Prompts
            </Button>
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-[380px_minmax(0,1fr)]">
            <div className="bg-white border rounded-2xl border-slate-200 shadow-sm">
              <div className="p-4 border-b border-slate-100">
                <div className="space-y-3">
                  <Button
                    onClick={handleCreateDraft}
                    className="w-full text-white bg-gradient-to-r from-violet-600 to-purple-700 hover:from-violet-700 hover:to-purple-800"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Demo Prompt
                  </Button>
                  <div className="relative">
                    <Search className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none left-3 top-1/2 text-slate-400" />
                    <Input
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.target.value)}
                      placeholder="Search demo agents..."
                      className="pl-9"
                    />
                  </div>
                </div>
              </div>

              <div className="max-h-[74vh] overflow-y-auto">
                {loadingPrompts ? (
                  <div className="flex items-center justify-center gap-2 px-4 py-10 text-sm text-slate-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading demo agents...
                  </div>
                ) : filteredPrompts.length === 0 ? (
                  <div className="px-4 py-10 text-sm text-center text-slate-500">
                    No demo agent prompts match the current search.
                  </div>
                ) : (
                  filteredPrompts.map((entry) => (
                    <button
                      key={entry.id}
                      type="button"
                      onClick={() => setSelectedPromptId(entry.id)}
                      className={`flex w-full items-start gap-3 border-b border-slate-100 px-4 py-4 text-left transition-colors last:border-b-0 ${
                        selectedPromptId === entry.id ? 'bg-violet-50' : 'hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center justify-center w-10 h-10 shrink-0 rounded-full bg-violet-100 text-violet-700">
                        <FileText className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold truncate text-slate-900">{entry.agent_name || 'Unnamed demo agent'}</div>
                        <div className="text-sm truncate text-slate-500">Industry: {entry.industry_id || '-'}</div>
                        <div className="mt-2 flex flex-wrap gap-2 text-xs">
                          <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-600">
                            {entry.domain || 'No domain'}
                          </span>
                          {entry.status ? (
                            <span className="rounded-full bg-emerald-50 px-2 py-0.5 font-medium text-emerald-700">
                              {entry.status}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>

            <div className="bg-white border rounded-2xl border-slate-200 shadow-sm">
              {loadingPrompts ? (
                <div className="flex min-h-[620px] items-center justify-center gap-2 text-sm text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading prompt details...
                </div>
              ) : !draft ? (
                <div className="flex min-h-[620px] items-center justify-center text-sm text-slate-500">
                  Select a demo agent prompt to edit.
                </div>
              ) : (
                <div className="p-6 space-y-6">
                  <div className="flex flex-col gap-4 pb-6 border-b border-slate-100 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <h2 className="text-xl font-semibold text-slate-900">
                        {draft.agent_name || (isCreatingPrompt ? 'Create Demo Prompt' : 'Demo Agent')}
                      </h2>
                      <p className="mt-1 text-sm text-slate-500">
                        {isCreatingPrompt ? 'New demo_agent_prompt record' : `${draft.industry_id || '-'} ${draft.domain ? `- ${draft.domain}` : ''}`}
                      </p>
                    </div>

                    <Button
                      onClick={handleSave}
                      disabled={saving}
                      className="text-white bg-gradient-to-r from-violet-600 to-purple-700 hover:from-violet-700 hover:to-purple-800"
                    >
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      {isCreatingPrompt ? 'Create Prompt' : 'Save Changes'}
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                    <div className="space-y-2">
                      <Label>Agent Name</Label>
                      <Input value={draft.agent_name} onChange={(event) => handleFieldChange('agent_name', event.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>Industry ID</Label>
                      <Input value={draft.industry_id} onChange={(event) => handleFieldChange('industry_id', event.target.value)} />
                    </div>
                    {!isCreatingPrompt ? (
                      <>
                        <div className="space-y-2">
                          <Label>Domain</Label>
                          <Input value={draft.domain} onChange={(event) => handleFieldChange('domain', event.target.value)} />
                        </div>
                        <div className="space-y-2">
                          <Label>Version Name</Label>
                          <Input value={draft.version_name} onChange={(event) => handleFieldChange('version_name', event.target.value)} />
                        </div>
                        <div className="space-y-2">
                          <Label>Status</Label>
                          <Input value={draft.status} onChange={(event) => handleFieldChange('status', event.target.value)} />
                        </div>
                        <div className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 px-4 py-3">
                          <div>
                            <Label>Calendar</Label>
                            <p className="mt-1 text-xs text-slate-500">Stored as boolean in Mongo.</p>
                          </div>
                          <Switch checked={!!draft.calendar} onCheckedChange={(checked) => handleFieldChange('calendar', checked)} />
                        </div>
                      </>
                    ) : null}
                  </div>

                  <div className="space-y-2">
                    <Label>First Message</Label>
                    <Textarea
                      value={draft.first_message}
                      onChange={(event) => handleFieldChange('first_message', event.target.value)}
                      className="min-h-[110px] resize-y font-mono text-sm leading-6"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>System Prompt</Label>
                    <Textarea
                      value={draft.system_prompt}
                      onChange={(event) => handleFieldChange('system_prompt', event.target.value)}
                      className="min-h-[440px] resize-y font-mono text-sm leading-6"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
